// src/context/preferences.js

const DEFAULTS = {
  "app.timezone": "UTC",
  "app.blobHeaderCollapsed": false,
  // All three detail panes open by default — the pane is where selection properties and actions live,
  // so starting closed hides the surface's controls until the user discovers the toggle.
  "blob:workflow.editorDetailsPane": true,
  "blob:workflow.debuggerDetailsPane": true,
  "blob:workflow.playgroundDetailsPane": true,
  "blob:workflow.editorWideContent": false,
  "blob:workflow.playgroundWideContent": false,
  "blob:workflow.defaultTab": "workflow",
  "blob:workflow.autoSelectRecentSession": false,
  // Reading size for a rendered markdown artifact in Mission Control. A factor, not a percentage.
  // Blob-scoped: reading size is a property of the reader, so it is set once for a body of work
  // rather than once per file.
  "blob:workflow.artifactZoom": 1
};

function storageKey(scope) {
  let key = "blobhub:prefs:me";
  if (scope && scope.orgId) {
    key += `:org:${scope.orgId}`;
  }
  if (scope && scope.blobId) {
    key += `:blob:${scope.blobId}`;
  }
  return key;
}

function readScope(scope) {
  try {
    const raw = localStorage.getItem(storageKey(scope));
    if (raw) {
      return JSON.parse(raw);
    }
  } catch (err) {
    // Corrupted JSON or storage error — return empty
  }
  return {};
}

function writeScope(scope, obj) {
  try {
    localStorage.setItem(storageKey(scope), JSON.stringify(obj));
  } catch (err) {
    // QuotaExceededError or storage disabled — fail silently
  }
}

/**
 * HOC that subscribes to preference changes and passes resolved values as props.
 *
 * @param {Component} WrappedComponent - component to wrap
 * @param {string[]} keys - preference keys to resolve (e.g. ["app.timezone"])
 * @param {function} [scopeFn] - (props) => scope object; defaults to {}
 *
 * Usage:
 *   export default withPreferences(MyComponent, ["app.timezone"], props => ({
 *     orgId: props.org.id, blobId: props.blob.id
 *   }));
 *
 * Injects a `preferences` prop: { "app.timezone": "US/Eastern", ... }
 * Re-renders only when watched values actually change.
 */
export function withPreferences(WrappedComponent, keys, scopeFn) {
  const React = require('react');

  class WithPreferences extends React.Component {
    constructor(props) {
      super(props);
      this.state = { prefs: this._resolve() };
    }

    _resolve() {
      const scope = scopeFn ? scopeFn(this.props) : {};
      const prefs = {};
      for (const key of keys) {
        prefs[key] = Preferences.get(key, scope);
      }
      return prefs;
    }

    _onPrefsChange = () => {
      const next = this._resolve();
      for (const key of keys) {
        if (next[key] !== this.state.prefs[key]) {
          this.setState({ prefs: next });
          return;
        }
      }
    }

    componentDidMount() {
      this._unsub = Preferences.subscribe(this._onPrefsChange);
    }

    componentWillUnmount() {
      if (this._unsub) this._unsub();
    }

    render() {
      return React.createElement(WrappedComponent, {
        ...this.props,
        preferences: this.state.prefs
      });
    }
  }

  WithPreferences.displayName =
    `WithPreferences(${WrappedComponent.displayName || WrappedComponent.name || 'Component'})`;
  return WithPreferences;
}

export default class Preferences {
  static _listeners = new Set();

  static subscribe(callback) {
    Preferences._listeners.add(callback);
    return () => Preferences._listeners.delete(callback);
  }

  static _notify() {
    for (const cb of Preferences._listeners) {
      try { cb(); } catch (err) { /* listener error */ }
    }
  }

  /**
   * Get a single preference value, resolved through scope overlay.
   * Resolution: defaults → user → org (if orgId) → blob (if blobId).
   */
  static get(key, scope) {
    const merged = Preferences.getAll(scope);
    return key in merged ? merged[key] : undefined;
  }

  /**
   * Get all preferences merged through scope overlay.
   */
  static getAll(scope) {
    const merged = { ...DEFAULTS, ...readScope({}) };
    if (scope && scope.orgId) {
      Object.assign(merged, readScope({ orgId: scope.orgId }));
    }
    if (scope && scope.orgId && scope.blobId) {
      Object.assign(merged, readScope({ orgId: scope.orgId, blobId: scope.blobId }));
    }
    return merged;
  }

  /**
   * Set a single key at the most specific scope level.
   */
  static set(key, value, scope) {
    const current = readScope(scope);
    current[key] = value;
    writeScope(scope, current);
    Preferences._notify();
  }

  /**
   * Get the raw flat map for a single scope level (no merge).
   */
  static getRaw(scope) {
    return readScope(scope);
  }

  /**
   * Replace the entire flat map for a scope level.
   */
  static setRaw(obj, scope) {
    writeScope(scope, obj);
    Preferences._notify();
  }

  /**
   * Remove a key from the most specific scope level.
   */
  static remove(key, scope) {
    const current = readScope(scope);
    delete current[key];
    writeScope(scope, current);
    Preferences._notify();
  }
}
